import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';

import { useTranslation } from 'react-i18next';
import { ButtonV3 } from 'components/ComponentV2';
import Icon, { IconName } from 'components/Icon';
import ActionMenu from 'components/ActionMenu';
import { useCandidate } from 'hooks/useCandidate';
import { useResponsive } from 'hooks/useResponsive';
import { useAIGeneratedResponse } from 'pages/candidate/applications/useAIGeneratedResponse';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import HamburgerNavMenu from '../HamburgerNavMenu/HamburgerNavMenu.component';
import CoverLetterInfo, {
  CoverLetterInfoRef,
} from './CoverLetterInfo/CoverLetterInfo.component';
import SplashScreen from './SplashScreen/SplashScreen.component';

import styles from './CoverLetterBuilder.module.scss';

export const CoverLetterBuilder: React.FC = () => {
  const router = useRouter();
  const [t] = useTranslation('candidate.cover-letter');
  const { opportunityId } = router.query;
  const screens = useResponsive();
  const { exportCoverLetterPDF, copyCoverLetter } = useAIGeneratedResponse();
  const { candidateDashboard } = useCandidateDashboard();
  const { candidate } = useAuth();
  const { markCoverLetterPopUpSeen } = useCandidate();
  const { show_cv_builder_popup } = candidate || {};
  const { profile_complete } = candidateDashboard || {};
  const builderStepsRef = useRef<HTMLDivElement>(null);
  const coverLetterViewerRef = useRef<HTMLDivElement>(null);

  const [coverLetterData, setCoverLetterData] = useState<string>('');
  const [_, setCompanyName] = useState<string | undefined>(undefined);
  const [showSplashScreen, setShowSplashScreen] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      setCoverLetterData('');
      setShowSplashScreen(false);
    };
  }, []);

  useEffect(() => {
    setShowSplashScreen(!!show_cv_builder_popup);
  }, [show_cv_builder_popup]);

  const actionMenuOption = useMemo(
    () => [
      {
        id: '1',
        label: t('cover-letter-builder.export'),
        icon: 'icon_download' as IconName,
        onClick: () => exportCoverLetterPDF(coverLetterData),
        disabled: !coverLetterData,
      },
      {
        id: '2',
        label: t('cover-letter-builder.copy'),
        icon: 'icon_copy' as IconName,
        onClick: () => copyCoverLetter(coverLetterData),
        disabled: !coverLetterData,
      },
    ],
    [coverLetterData]
  );

  const goToEditProfile = () => {
    router.push(PAGE_ROUTES.CANDIDATE_PROFILE);
  };

  const onCloseSplashScreen = () => {
    markCoverLetterPopUpSeen();
    setShowSplashScreen(false);
  };

  const saveCoverLetterData = (coverLetter: string, companyTitle?: string) => {
    setCoverLetterData(coverLetter);
    setCompanyName(companyTitle);
  };

  const getProfileBannerWidth = () => {
    const { clientWidth: builderWidth = 360 } = builderStepsRef.current || {};
    const { clientWidth: viewerWidth = 760 } =
      coverLetterViewerRef.current || {};

    return builderWidth + viewerWidth + 24;
  };

  const renderCompleteProfileBanner = useMemo(
    () => (
      <div
        className={styles.completeProfileBanner}
        style={{ width: screens.sm ? '100%' : `${getProfileBannerWidth()}px` }}
      >
        <div className={styles.info}>
          <Icon iconName="icon_info" size="large" className={styles.infoIcon} />
          <div>
            <p className={styles.infoTitle}>
              {t('cover-letter-builder.complete-profile-to-tailor')}
            </p>
            <p className={styles.infoCaption}>
              {t('cover-letter-builder.info-caption')}
            </p>
            {screens.sm && (
              <ButtonV3 size="small" onClick={goToEditProfile}>
                {t('cover-letter-builder.complete-profile')}
              </ButtonV3>
            )}
          </div>
        </div>
        {!screens.sm && (
          <ButtonV3 onClick={goToEditProfile}>
            {t('cover-letter-builder.complete-profile')}
          </ButtonV3>
        )}
      </div>
    ),
    [screens]
  );

  const renderPageContent = useMemo(
    () => (
      <div className={styles.CoverLetterBuilderContent}>
        {!profile_complete && renderCompleteProfileBanner}
        <CoverLetterInfo
          opportunityId={opportunityId as string}
          goToEditProfile={goToEditProfile}
          setCoverLetterData={saveCoverLetterData}
          ref={
            {
              builderStepsRef: builderStepsRef,
              coverLetterViewerRef: coverLetterViewerRef,
            } as unknown as RefObject<CoverLetterInfoRef>
          }
        />
      </div>
    ),
    [profile_complete, opportunityId, renderCompleteProfileBanner]
  );

  return (
    <section className={styles.CoverLetterBuilder}>
      {showSplashScreen && (
        <SplashScreen
          isProfileComplete={!!profile_complete}
          onClose={onCloseSplashScreen}
        />
      )}
      {screens.sm && (
        <div className={styles.CoverLetterBuilderHeader}>
          <HamburgerNavMenu
            triggerClassName={styles.navMenuTrigger}
            menuListClassName={styles.navMenuList}
          />
          <p className={styles.titleText}>
            {t('cover-letter-builder.generate-cover-letter')}
          </p>
          <div className={styles.action}>
            <ActionMenu
              options={actionMenuOption}
              listClassName={cn(styles.actionMenu, {
                [styles.disableMenu]: !coverLetterData,
              })}
            />
          </div>
        </div>
      )}
      {renderPageContent}
    </section>
  );
};
